import { useCallback, useEffect, useState } from 'react';
import { trackAmplitudeEvent, flushAmplitudeEvents } from '../../config/amplitude';

export type ChartLayout = 'classic' | 'modern';

const STORAGE_KEY = 'stabletrack.chartLayout';
const SWITCH_TS_KEY = 'stabletrack.chartLayout.switchedAt';
const STUCK_FIRED_KEY = 'stabletrack.chartLayout.stuckFiredFor';
const VIEW_FIRED_KEY = 'stabletrack.chartLayout.viewFiredForSession';
const STUCK_DAYS_THRESHOLD = 7;

// Default = classic so existing users see the layout they already know. The
// modern layout becomes available via the toggle pill in the header.
const DEFAULT_LAYOUT: ChartLayout = 'classic';

function readStored(): ChartLayout {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'modern' ? 'modern' : 'classic';
  } catch {
    return DEFAULT_LAYOUT;
  }
}

function readSwitchedAt(): number | null {
  try {
    const v = localStorage.getItem(SWITCH_TS_KEY);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function daysBetween(a: number, b: number): number {
  return Math.floor((b - a) / (1000 * 60 * 60 * 24));
}

/**
 * Reads + writes the chart layout preference and emits Amplitude events:
 *
 *   chart_layout_viewed   — once per browser session, on first read
 *   chart_layout_switched — every toggle, with { from, to }
 *   chart_layout_stuck    — once per "stay" of ≥7 days on a non-default layout
 *
 * Storage lives in localStorage so the preference survives reloads but is
 * scoped per browser/device. Swap to a backend-stored user preference later
 * if cross-device stickiness becomes important.
 */
export function useChartLayout(): {
  layout: ChartLayout;
  setLayout: (next: ChartLayout) => void;
} {
  const [layout, setLayoutState] = useState<ChartLayout>(readStored);

  // Fire view event once per session (sessionStorage = cleared on tab close).
  useEffect(() => {
    try {
      const sessionMarker = sessionStorage.getItem(VIEW_FIRED_KEY);
      if (sessionMarker === layout) return;
      sessionStorage.setItem(VIEW_FIRED_KEY, layout);
      trackAmplitudeEvent('chart_layout_viewed', { layout });
    } catch {
      // Quota or disabled storage — silently skip telemetry.
    }

    // "Stuck" event: if the user switched away from default AND has been on
    // the current layout for >= STUCK_DAYS_THRESHOLD days, emit once.
    try {
      const switchedAt = readSwitchedAt();
      if (switchedAt && layout !== DEFAULT_LAYOUT) {
        const days = daysBetween(switchedAt, Date.now());
        if (days >= STUCK_DAYS_THRESHOLD) {
          const stuckMarker = localStorage.getItem(STUCK_FIRED_KEY);
          // Marker shape: "{layout}:{switchedAt}". Re-fires only if the user
          // switches again (new switchedAt) so we don't spam.
          const expected = `${layout}:${switchedAt}`;
          if (stuckMarker !== expected) {
            trackAmplitudeEvent('chart_layout_stuck', { layout, days_since_switch: days });
            localStorage.setItem(STUCK_FIRED_KEY, expected);
          }
        }
      }
    } catch {
      // Same as above.
    }
  }, [layout]);

  const setLayout = useCallback((next: ChartLayout) => {
    const prev = readStored();
    if (prev === next) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
      localStorage.setItem(SWITCH_TS_KEY, String(Date.now()));
      sessionStorage.removeItem(VIEW_FIRED_KEY);
    } catch {
      // Best-effort; reload below will still flip the layout via localStorage default.
    }
    trackAmplitudeEvent('chart_layout_switched', { from: prev, to: next });
    setLayoutState(next);
    // Force a clean reload so every component (including the shell that
    // selects which renderer to mount) picks up the new value. The two hook
    // instances would otherwise hold stale local state; localStorage carries
    // the new value across the reload.
    //
    // track() only enqueues the switch event, so reloading immediately would
    // tear down the in-memory queue before it's delivered — losing the single
    // most important adoption metric. Flush first, but cap the wait so a slow
    // network never strands the user on the old layout.
    const reload = () => window.location.reload();
    Promise.race([
      flushAmplitudeEvents(),
      new Promise<void>((resolve) => setTimeout(resolve, 800)),
    ]).then(reload, reload);
  }, []);

  return { layout, setLayout };
}
